<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import type { MenuItem } from '@/types/layout'

defineOptions({ name: 'AppMenuItem' })

const props = withDefaults(defineProps<Props>(), {
  index: 0,
  root: true,
  parentItemKey: null,
})

interface Props {
  item: MenuItem
  index?: number
  root?: boolean
  parentItemKey?: string | null
}

const route = useRoute()
const { layoutState, menuRouteContext, setActiveMenuItem, toggleMenu } = useLayout()
const isActiveMenu = ref(false)
const itemKey = ref<string>()

onBeforeMount(() => {
  itemKey.value = props.parentItemKey
    ? `${props.parentItemKey}-${props.index}`
    : String(props.index)

  const activeItem = layoutState.activeMenuItem
  isActiveMenu.value =
    activeItem === itemKey.value ||
    (activeItem ? activeItem.startsWith(`${itemKey.value}-`) : false)
})

watch(
  () => layoutState.activeMenuItem,
  (newVal: string | null) => {
    isActiveMenu.value =
      newVal === itemKey.value || newVal?.startsWith(`${itemKey.value}-`) || false
  },
)

function itemClick(event: MouseEvent, item: MenuItem) {
  if (item.to && (layoutState.staticMenuMobileActive || layoutState.overlayMenuActive)) {
    toggleMenu()
  }

  if (item.command) {
    item.command({ originalEvent: event, item })
  }

  const foundItemKey = item.items
    ? isActiveMenu.value
      ? props.parentItemKey
      : itemKey
    : itemKey.value
  setActiveMenuItem(foundItemKey)
}

// Pages reachable from several sections can redirect the marker to their own section
const activePath = computed(() => {
  const context = menuRouteContext.value
  return context && route.path.startsWith(context.routePrefix) ? context.menuPath : route.path
})

function checkActiveRoute(item: MenuItem) {
  const path = activePath.value
  return item.to && path.includes(item.to) && (item.to !== '/' || path === item.to)
}
</script>

<template>
  <li v-if="item.separator" class="layout-menu-separator">
    <hr class="my-2 h-px border-0 bg-neutral-200 dark:bg-neutral-700" />
  </li>
  <li v-else :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }">
    <!-- Root menu items -->
    <div
      v-if="root"
      class="layout-menuitem-root-text flex items-center justify-between"
      :class="[item.class, { 'active-route': checkActiveRoute(item) }]"
    >
      <!-- Root item with submenu that can be toggled -->
      <a
        v-if="!item.to && item.items && !item.noToggle"
        :class="[item.class, { 'active-route': checkActiveRoute(item) }]"
        class="flex w-full cursor-pointer items-center justify-between"
        tabindex="0"
        @click="itemClick($event, item)"
      >
        <span class="flex items-center gap-2">
          <UIcon v-if="item.icon" :name="item.icon" class="layout-menuitem-icon" />
          {{ item.label }}
        </span>
        <UIcon name="i-lucide-chevron-down" class="layout-submenu-toggler" />
      </a>
      <!-- Root item with submenu that cannot be toggled (noToggle) -->
      <template v-else-if="!item.to && item.items && item.noToggle">
        <UIcon v-if="item.icon" :name="item.icon" class="layout-menuitem-icon" />
        {{ item.label }}
      </template>
      <!-- Root item with link -->
      <router-link
        v-else-if="item.to"
        :to="item.to"
        :class="[item.class, { 'active-route': checkActiveRoute(item) }]"
        tabindex="0"
        @click="itemClick($event, item)"
      >
        <span class="flex items-center gap-2">
          <UIcon v-if="item.icon" :name="item.icon" class="layout-menuitem-icon" />
          {{ item.label }}
        </span>
        <span class="ml-auto flex items-center gap-2">
          <UBadge
            v-if="item.badge && Number(item.badge) > 0"
            :label="item.badge"
            color="info"
            variant="subtle"
            size="sm"
          />
          <UIcon
            name="i-lucide-chevron-down"
            v-if="item.items && !item.noToggle"
            class="layout-submenu-toggler"
          />
        </span>
      </router-link>
    </div>

    <!-- Non-root menu items (submenu children) -->
    <router-link
      v-if="!root && item.to"
      :class="[item.class, { 'active-route': checkActiveRoute(item) }]"
      tabindex="0"
      class="pl-7!"
      :to="item.to"
      @click="itemClick($event, item)"
    >
      <UIcon v-if="item.icon" :name="item.icon" class="shrink-0" />
      <span class="layout-menuitem-text" :class="!item.icon ? 'pl-4' : ''">{{ item.label }}</span>
    </router-link>

    <!-- Submenu items -->
    <Transition v-if="item.items" name="layout-submenu">
      <div v-show="item.noToggle || isActiveMenu" class="layout-submenu-wrapper">
        <ul class="layout-submenu">
          <AppMenuItem
            v-for="(child, i) in item.items"
            :key="`${itemKey}-${i}`"
            :index="i"
            :item="child"
            :parent-item-key="itemKey"
            :root="false"
          />
        </ul>
      </div>
    </Transition>
  </li>
</template>
